import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Clock, Download, Search, X, AlertTriangle, BarChart3, Info } from 'lucide-react';

import { cn } from '@/lib/utils';
import { useT, useLang } from '@/i18n';
import { exportCsv, csvFilename } from '@/lib/csv';
import { useAdminAgentsProgressOverview } from '@/features/agent-progress/progressHooks';
import { useAdminUploadersProgressOverview } from '@/features/uploader-progress/progressHooks';
import type { TierName } from '@/features/agent-progress/tierConstants';
import { useReportsByAgent, useReportsByUploader } from '@/features/admin/reports.api';
import { useSystemConfigList, useTierThresholdsHistory } from '@/features/system-config/system-config.hooks';
import { EmptyState, ErrorState, Skeleton } from '@/components/chassis/States';
import { Pager } from '@/components/chassis/Pager';
import { AgentProgressPanel } from './components/AgentProgressPanel';
import { UploaderDetailModal } from './components/UploaderDetailModal';

/**
 * F5-T6a · Admin 03 — «Progreso de agentes» (contrato 2026-08-28).
 *
 * Metas, niveles y progreso por persona. Pantalla que toca nómina: UNA sola
 * escalera, UNA sola meta, y ninguna cifra de dinero — conteos y niveles.
 *
 * MUEREN (contrato §dies): la escalera semanal con nombres de metal (lo
 * semanal es «ritmo de la semana», conteo contra la meta, NUNCA un metal) ·
 * la segunda meta diaria (queda la vigente, de system_config) · la torta de
 * niveles (barra apilada: el largo compara, el ángulo no) · toda estimación
 * de bono/tarifa/monto · la escalera para uploaders (cuenta cargas y nada
 * más) · los colores metálicos (4 intensidades del mismo teal).
 *
 * DESVIACIONES DECLARADAS (suben al CEO):
 * - El catálogo del dibujo dice Bronce/Plata/Oro/«Platino»; el sistema real
 *   tiene 5 niveles con «Diamante» arriba y «Camino a Bronce» abajo
 *   (tier.caminoBronce). Se conserva el vocabulario REAL (i18n tier.*) y el
 *   tramo below_floor entra a la distribución con swatch neutro — ocultarlo
 *   mentiría la foto. Renombrar Diamante→Platino toca nómina: decisión CEO.
 * - Los umbrales y la meta salen SIEMPRE de system_config (F-4/F-5); los
 *   0–99/15 del dibujo son datos demo del spec.
 */

type Tab = 'agents' | 'uploaders';
type PeriodId = 'current' | 'previous';

const TIER_SEQ: TierName[] = ['below_floor', 'base', 't1', 't2', 't3'];

/** 4 intensidades del mismo teal (mockup, sin token — literales declarados);
 *  below_floor va en neutro nav-card (fuera del dibujo, ver cabecera). */
const LADDER_COLOR: Record<TierName, string> = {
  below_floor: '#e3e8eb',
  base: '#cfe4e5',
  t1: '#8fc3c4',
  t2: '#4f9fa1',
  t3: '#228a8d',
};

const TIER_LABEL_KEY = {
  below_floor: 'tier.caminoBronce',
  base: 'tier.bronce',
  t1: 'tier.plata',
  t2: 'tier.oro',
  t3: 'tier.diamante',
} as const;

interface Ladder {
  base: number;
  t1: number;
  t2: number;
  t3: number;
}

interface Row {
  /** id estable de la fila (string para cubrir agentes y uploaders). */
  id: string;
  /** id numérico para el drill-down de agente (ProgressModal admin). */
  numId: number | null;
  name: string;
  total: number;
  tier: TierName | null;
}

function initials(name: string): string {
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0]?.toUpperCase() ?? '').join('');
}

function isoDate(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Derivación del contrato: tier = tramo más alto con total ≥ min. */
function tierForTotal(total: number, ladder: Ladder): TierName {
  if (total >= ladder.t3) return 't3';
  if (total >= ladder.t2) return 't2';
  if (total >= ladder.t1) return 't1';
  if (total >= ladder.base) return 'base';
  return 'below_floor';
}

const PAGE_SIZE = 25;

export default function AgentsProgressPage(): JSX.Element {
  const t = useT();
  const lang = useLang();
  const [params, setParams] = useSearchParams();

  const [tab, setTab] = useState<Tab>('agents');
  const [period, setPeriod] = useState<PeriodId>('current');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [drillAgent, setDrillAgent] = useState<number | null>(null);
  const [drillUploader, setDrillUploader] = useState<string | null>(null);

  // Filtro por nivel — vive en ?tier= para conservar los deep-links que ya
  // existían hacia esta pantalla (CONSERVA).
  const tierFilter = ((): TierName | null => {
    const v = params.get('tier');
    return v && (TIER_SEQ as string[]).includes(v) ? (v as TierName) : null;
  })();
  const setTierFilter = (next: TierName | null): void => {
    const p = new URLSearchParams(params);
    if (next == null) p.delete('tier');
    else p.set('tier', next);
    setParams(p, { replace: true });
    setPage(0);
  };

  // ---- Períodos: mes actual / mes anterior, con fechas reales ----
  const todayD = new Date();
  const curStart = new Date(todayD.getFullYear(), todayD.getMonth(), 1);
  const prevStart = new Date(todayD.getFullYear(), todayD.getMonth() - 1, 1);
  const prevEnd = new Date(todayD.getFullYear(), todayD.getMonth(), 0);
  const monthFmt = new Intl.DateTimeFormat(lang === 'es' ? 'es' : 'en', { month: 'long' });
  const cap = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1);
  const curLabel = `${cap(monthFmt.format(curStart))} ${curStart.getFullYear()}`;
  const prevLabel = `${cap(monthFmt.format(prevStart))} ${prevStart.getFullYear()}`;
  const periodWord = monthFmt.format(period === 'current' ? curStart : prevStart);

  // Semana vigente (lunes → hoy) para «ritmo de la semana».
  const monday = new Date(todayD);
  monday.setDate(todayD.getDate() - ((todayD.getDay() + 6) % 7));

  // ---- Datos ----
  const overviewQ = useAdminAgentsProgressOverview();
  const uploadersQ = useAdminUploadersProgressOverview();
  const byAgentPrevQ = useReportsByAgent(isoDate(prevStart), isoDate(prevEnd));
  const byAgentWeekQ = useReportsByAgent(isoDate(monday), isoDate(todayD));
  const byUploaderPrevQ = useReportsByUploader(isoDate(prevStart), isoDate(prevEnd));
  const byUploaderWeekQ = useReportsByUploader(isoDate(monday), isoDate(todayD));

  // Config: la meta y los umbrales VIGENTES (F-4/F-5 — nunca hardcodeados).
  const cfgQ = useSystemConfigList();
  const cfg = useMemo(() => {
    const m = new Map<string, unknown>();
    for (const it of cfgQ.data?.items ?? []) m.set(it.key, it.value);
    return m;
  }, [cfgQ.data]);
  const cfgInt = (key: string): number | null => {
    const v = cfg.get(key);
    const n = typeof v === 'number' ? v : Number(v);
    return Number.isFinite(n) && n > 0 ? n : null;
  };
  const dailyTarget = cfgInt('agent_daily_target');
  const uploaderTarget = cfgInt('uploader_daily_target');
  const ladder = useMemo<Ladder | null>(() => {
    const v = cfg.get('agent_tier_thresholds') as Partial<Ladder> | undefined;
    if (!v || [v.base, v.t1, v.t2, v.t3].some((x) => typeof x !== 'number')) return null;
    return { base: v.base!, t1: v.t1!, t2: v.t2!, t3: v.t3! };
  }, [cfg]);

  // Variante ámbar de la barra de meta: cambio PROGRAMADO (fila de historial
  // con effectiveDate futura — la vigencia real la aplica el server, T1b).
  const targetKey = tab === 'agents' ? 'agent_daily_target' : 'uploader_daily_target';
  const histQ = useTierThresholdsHistory(targetKey);
  const scheduled = useMemo(() => {
    const todayIso = isoDate(new Date());
    const fut = (histQ.data?.items ?? [])
      .filter((r) => r.effectiveDate > todayIso)
      .sort((a, b) => (a.effectiveDate < b.effectiveDate ? -1 : 1));
    return fut[0] ?? null;
  }, [histQ.data]);

  // ---- Filas por pestaña y período ----
  const rows = useMemo<Row[]>(() => {
    if (tab === 'agents') {
      if (period === 'current') {
        return (overviewQ.data?.agents ?? [])
          .filter((a) => a.isActive)
          .map((a) => ({
            id: String(a.agentId),
            numId: a.agentId,
            name: a.displayName,
            total: a.screeningsMonth,
            // Nivel del período vigente: el del server (ancla as-of incluida).
            tier: a.tierDisplayed,
          }));
      }
      // Mes anterior: totales reales de reports/by-agent; el nivel se DERIVA
      // con la escalera vigente. NECESITA-SERVER: resolver los umbrales as-of
      // del período cerrado (el ancla F-2 vive en el server) — con umbrales
      // sin cambios recientes la derivación coincide.
      return (byAgentPrevQ.data ?? []).map((r) => ({
        id: String(r.agentId),
        numId: r.agentId,
        name: r.agentName,
        total: r.screeningsSubmitted,
        tier: ladder ? tierForTotal(r.screeningsSubmitted, ladder) : null,
      }));
    }
    // Uploaders: SIN escalera (contrato) — tier no se usa en esta pestaña.
    if (period === 'current') {
      return (uploadersQ.data?.items ?? [])
        .filter((u) => u.active)
        .map((u) => ({ id: u.uploaderId, numId: null, name: u.displayName, total: u.uploadsMonth, tier: null }));
    }
    return (byUploaderPrevQ.data ?? []).map((r) => ({
      id: String(r.uploaderId),
      numId: null,
      name: r.uploaderName,
      total: r.uploaded,
      tier: null,
    }));
  }, [tab, period, overviewQ.data, byAgentPrevQ.data, uploadersQ.data, byUploaderPrevQ.data, ladder]);

  // «Ritmo de la semana» = semana VIGENTE contra la meta. En un mes cerrado
  // no hay semana en curso → guion (persona sin dato ≠ cero, contrato).
  const weekById = useMemo(() => {
    const m = new Map<string, number>();
    if (period !== 'current') return m;
    if (tab === 'agents') for (const r of byAgentWeekQ.data ?? []) m.set(String(r.agentId), r.screeningsSubmitted);
    else for (const r of byUploaderWeekQ.data ?? []) m.set(String(r.uploaderId), r.uploaded);
    return m;
  }, [tab, period, byAgentWeekQ.data, byUploaderWeekQ.data]);

  const distribution = useMemo(() => {
    const c: Record<TierName, number> = { below_floor: 0, base: 0, t1: 0, t2: 0, t3: 0 };
    if (tab !== 'agents') return c;
    for (const r of rows) if (r.tier) c[r.tier]++;
    return c;
  }, [rows, tab]);

  const visible = useMemo(() => {
    let out = [...rows].sort((a, b) => b.total - a.total);
    if (tab === 'agents' && tierFilter) out = out.filter((r) => r.tier === tierFilter);
    // CONSERVA: la búsqueda por nombre existía en la pantalla vieja; el
    // contrato ni la dibuja ni la mata (precedente E-23).
    const q = search.trim().toLowerCase();
    if (q) out = out.filter((r) => r.name.toLowerCase().includes(q));
    return out;
  }, [rows, tab, tierFilter, search]);

  const pages = Math.max(1, Math.ceil(visible.length / PAGE_SIZE));
  const pageRows = visible.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const pageClamped = Math.min(page, pages - 1);

  const target = tab === 'agents' ? dailyTarget : uploaderTarget;
  const weekGoal = target != null ? target * 5 : null;

  const isLoading =
    (tab === 'agents' && period === 'current' && overviewQ.isLoading) ||
    (tab === 'agents' && period === 'previous' && byAgentPrevQ.isLoading) ||
    (tab === 'uploaders' && period === 'current' && uploadersQ.isLoading) ||
    (tab === 'uploaders' && period === 'previous' && byUploaderPrevQ.isLoading);
  const loadError =
    tab === 'agents'
      ? period === 'current'
        ? overviewQ.error
        : byAgentPrevQ.error
      : period === 'current'
        ? uploadersQ.error
        : byUploaderPrevQ.error;

  const switchTab = (next: Tab): void => {
    // Cambiar de rol resetea filtro de nivel y selección (contrato).
    setTab(next);
    setTierFilter(null);
    setDrillAgent(null);
    setDrillUploader(null);
    setSearch('');
    setPage(0);
  };

  const handleExport = (): void => {
    // Export CSV: las MISMAS columnas de la tabla vigente (pestaña + filtro),
    // SIN dinero. NECESITA-SERVER: la auditoría del export (quién/cuándo/
    // con qué filtro) — este export es client-side y no la dispara; la regla
    // se generaliza en la ventana 7.
    if (tab === 'agents') {
      exportCsv<Row>({
        filename: csvFilename('agents-progress'),
        columns: [
          { header: t('f5t6a.ap.col.person'), accessor: (r) => r.name },
          { header: t('f5t6a.ap.col.month'), accessor: (r) => r.total },
          { header: t('f5t6a.ap.col.tier'), accessor: (r) => (r.tier ? t(TIER_LABEL_KEY[r.tier]) : '—') },
          { header: t('f5t6a.ap.col.toNext'), accessor: (r) => toNextLabel(r) },
          { header: t('f5t6a.ap.col.pace'), accessor: (r) => paceText(r) ?? '—' },
        ],
        rows: visible,
      });
    } else {
      exportCsv<Row>({
        filename: csvFilename('uploaders-progress'),
        columns: [
          { header: t('f5t6a.ap.col.person'), accessor: (r) => r.name },
          { header: t('f5t6a.ap.col.uploadsMonth'), accessor: (r) => r.total },
          { header: t('f5t6a.ap.col.pace'), accessor: (r) => paceText(r) ?? '—' },
        ],
        rows: visible,
      });
    }
  };

  const toNextLabel = (r: Row): string => {
    if (!ladder || r.tier == null) return '—';
    if (r.tier === 't3') return t('f5t6a.ap.ladderTop');
    const next = TIER_SEQ[TIER_SEQ.indexOf(r.tier) + 1] as Exclude<TierName, 'below_floor'>;
    const n = Math.max(0, ladder[next] - r.total);
    return t('f5t6a.ap.toNextVal', { n, tier: t(TIER_LABEL_KEY[next]) });
  };

  const paceText = (r: Row): string | null => {
    const week = weekById.get(r.id);
    if (week == null || weekGoal == null || weekGoal <= 0) return null;
    return `${week} / ${weekGoal} · ${Math.round((week / weekGoal) * 100)}%`;
  };

  const openRow = (r: Row): void => {
    // Click en una fila abre los screenings de esa persona (drill-down
    // CONSERVADO: ProgressModal admin / UploaderDetailModal).
    if (tab === 'agents' && r.numId != null) setDrillAgent(r.numId);
    else if (tab === 'uploaders') setDrillUploader(r.id);
  };

  const capsuleBtn = (on: boolean): string =>
    cn(
      'min-h-[44px] px-[18px] rounded-lg font-head text-[13px] font-bold transition-colors duration-150 motion-reduce:transition-none',
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal focus-visible:ring-offset-2',
      on ? 'bg-nav-navy text-white hover:bg-nav-navyHover' : 'text-nav-slate hover:bg-nav-soft hover:text-nav-navy',
    );

  const footLabel =
    visible.length === 1
      ? t('f5t6a.foot.person', { p: pageClamped + 1, pp: pages })
      : t('f5t6a.foot.people', { n: visible.length, p: pageClamped + 1, pp: pages });

  return (
    <div className="max-w-[1440px] mx-auto font-body text-nav-navy animate-fade-up-slow motion-reduce:animate-none">
      {tab === 'agents' && drillAgent != null ? (
        /* F5-R8c — la ficha «Progreso del agente» REEMPLAZA el drill-down
           modal (ProgressModal admin, dos gráficos vacíos y medallas
           grises): panel de ancho completo en lugar de la tabla, con
           «Todos los agentes» para volver. Uploaders conserva su modal. */
        <AgentProgressPanel
          agentId={drillAgent}
          onBack={() => setDrillAgent(null)}
          fallbackLadder={ladder}
        />
      ) : (
        <>
      {/* ---- Fila de conmutadores: rol + período ---- */}
      <div className="flex items-center gap-5 flex-wrap mb-6">
        <div className="inline-flex bg-white border-[1.5px] border-nav-card rounded-[10px] p-[3px] gap-[2px]" role="group">
          <button type="button" aria-pressed={tab === 'agents'} onClick={() => switchTab('agents')} className={capsuleBtn(tab === 'agents')}>
            {t('f5t6a.ap.tabAgents')}
          </button>
          <button type="button" aria-pressed={tab === 'uploaders'} onClick={() => switchTab('uploaders')} className={capsuleBtn(tab === 'uploaders')}>
            {t('f5t6a.ap.tabUploaders')}
          </button>
        </div>
        <div className="ml-auto flex items-center gap-3">
          <span className="font-head text-[10.5px] font-bold tracking-[0.14em] uppercase text-nav-slate">
            {t('f5t6a.ap.period')}
          </span>
          <div className="inline-flex bg-white border-[1.5px] border-nav-card rounded-[10px] p-[3px] gap-[2px]" role="group">
            <button
              type="button"
              aria-pressed={period === 'current'}
              onClick={() => { setPeriod('current'); setPage(0); }}
              className={capsuleBtn(period === 'current')}
            >
              {curLabel}
            </button>
            <button
              type="button"
              aria-pressed={period === 'previous'}
              onClick={() => { setPeriod('previous'); setPage(0); }}
              className={capsuleBtn(period === 'previous')}
            >
              {prevLabel}
            </button>
          </div>
        </div>
      </div>

      {/* El cambio de período y el conteo filtrado se anuncian (a11y). */}
      <span aria-live="polite" className="sr-only">
        {periodWord} · {footLabel}
      </span>

      {/* ---- Barra de meta (la vigente, de system_config — F-5). La variante
              ámbar anuncia un cambio PROGRAMADO: lo único que puede exigir
              una acción en esta pantalla. ---- */}
      <div
        className={cn(
          'flex items-center gap-2.5 flex-wrap rounded-xl border-[1.5px] px-[18px] py-3.5 mb-3.5 text-[13.5px] font-semibold',
          scheduled
            ? 'bg-nav-amberTint border-nav-amber text-nav-amberText'
            : 'bg-nav-tealTint border-nav-teal text-nav-tealDark',
        )}
      >
        <Clock className="w-[17px] h-[17px] flex-none" strokeWidth={2} aria-hidden />
        <span className="min-w-0 flex-1">
          {target != null
            ? t(tab === 'agents' ? 'f5t6a.ap.goalAgents' : 'f5t6a.ap.goalUploaders', { n: target })
            : '—'}
          {scheduled && (
            <span className="block mt-0.5">
              {t('f5t6a.ap.goalScheduled', { v: String(scheduled.newValue), d: scheduled.effectiveDate })}
            </span>
          )}
        </span>
        <Link
          to="/admin/settings?group=metas"
          className="inline-flex items-center gap-1 font-body text-[13px] font-semibold flex-none hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal focus-visible:ring-offset-2 rounded"
        >
          {t('f5t6a.ap.seeConfig')} <span aria-hidden>→</span>
        </Link>
      </div>

      {/* ---- Distribución de niveles — SOLO Agentes; barra apilada, no torta ---- */}
      {tab === 'agents' && (
        <div className="bg-white border-[1.5px] border-nav-card rounded-xl px-6 py-[22px] mb-3.5">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <h2 className="m-0 font-head text-[10.5px] font-bold tracking-[0.14em] uppercase text-nav-teal">
              {t('f5t6a.ap.distTitle', { period: periodWord })}
            </h2>
            <span className="text-[13px] text-nav-slate tabular-nums">
              {t('f5t6a.ap.distMeta', { n: rows.length })}
            </span>
          </div>
          <div className="h-3.5 rounded-[7px] overflow-hidden bg-nav-hair flex mt-4" aria-hidden={rows.length === 0}>
            {TIER_SEQ.map((tier) => {
              const count = distribution[tier];
              const pct = rows.length > 0 ? (count / rows.length) * 100 : 0;
              if (count === 0) return null;
              return (
                <div
                  key={tier}
                  title={`${t(TIER_LABEL_KEY[tier])}: ${count}`}
                  className="h-full transition-[width] duration-300 ease-[cubic-bezier(.4,0,.2,1)] motion-reduce:transition-none"
                  style={{ width: `${pct}%`, backgroundColor: LADDER_COLOR[tier] }}
                />
              );
            })}
          </div>
          <div className="flex gap-2.5 flex-wrap mt-[18px]" role="group" aria-label={t('f5t6a.ap.filterAria')}>
            {TIER_SEQ.map((tier) => {
              const on = tierFilter === tier;
              return (
                <button
                  key={tier}
                  type="button"
                  aria-pressed={on}
                  onClick={() => setTierFilter(on ? null : tier)}
                  className={cn(
                    'inline-flex items-center gap-2 min-h-[44px] px-3.5 rounded-lg border-[1.5px] font-head text-[13px] font-bold text-nav-navy',
                    'transition-colors duration-150 motion-reduce:transition-none',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal focus-visible:ring-offset-2',
                    on ? 'bg-nav-tealTint border-nav-teal' : 'bg-white border-nav-card hover:bg-nav-tealTint hover:border-nav-teal',
                  )}
                >
                  <span aria-hidden className="w-[11px] h-[11px] rounded-[3px] flex-none" style={{ backgroundColor: LADDER_COLOR[tier] }} />
                  {t(TIER_LABEL_KEY[tier])}
                  <span className="text-nav-slate font-semibold tabular-nums">{distribution[tier]}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* ---- La tabla ---- */}
      <div className="bg-white border-[1.5px] border-nav-card rounded-xl overflow-hidden">
        <div className="flex items-center gap-2.5 flex-wrap px-6 pt-5 pb-4">
          <h2 className="m-0 font-head text-[10.5px] font-bold tracking-[0.14em] uppercase text-nav-teal">
            {t(tab === 'agents' ? 'f5t6a.ap.byAgent' : 'f5t6a.ap.byUploader')}
          </h2>
          <div className="ml-auto flex items-center gap-2 flex-wrap">
            {/* CONSERVA: búsqueda por nombre (pantalla vieja, precedente E-23). */}
            <span className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-nav-faint" aria-hidden />
              <input
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                placeholder={t('admin.agentsProgress.searchPlaceholder')}
                aria-label={t('admin.agentsProgress.searchPlaceholder')}
                className="min-h-[44px] pl-8 pr-3 rounded-lg border-[1.5px] border-nav-card bg-white text-[13px] text-nav-navy placeholder:text-nav-faint focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal focus-visible:ring-offset-2 w-[180px]"
              />
            </span>
            {tab === 'agents' && tierFilter && (
              <button
                type="button"
                onClick={() => setTierFilter(null)}
                className="inline-flex items-center gap-1.5 min-h-[44px] px-3 rounded-lg font-head text-[13px] font-semibold text-nav-teal hover:bg-nav-tealTint transition-colors duration-150 motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal focus-visible:ring-offset-2"
              >
                <X className="w-3.5 h-3.5" aria-hidden />
                {t('f5t6a.lm.clearFilter')}
              </button>
            )}
            <button
              type="button"
              onClick={handleExport}
              disabled={visible.length === 0}
              className="inline-flex items-center gap-1.5 min-h-[44px] px-4 rounded-lg border-[1.5px] border-nav-line bg-white font-head text-[13px] font-bold text-nav-navy hover:bg-nav-tealTint hover:border-nav-teal hover:text-nav-tealDark transition-colors duration-150 motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal focus-visible:ring-offset-2 disabled:opacity-40"
            >
              <Download className="w-4 h-4" aria-hidden />
              {t('f5t6a.ap.export')}
            </button>
          </div>
        </div>

        {isLoading ? (
          <div className="px-6 pb-6 space-y-2.5" aria-busy="true">
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <Skeleton key={i} className="h-[56px] rounded-lg" />
            ))}
          </div>
        ) : loadError ? (
          <ErrorState
            icon={<AlertTriangle className="w-6 h-6" aria-hidden />}
            title={t('f5t6a.ap.errTitle')}
            body={t('f5t6a.ap.errBody')}
            action={{
              label: t('f5t6a.lm.retry'),
              onClick: () => {
                void overviewQ.refetch();
                void uploadersQ.refetch();
                void byAgentPrevQ.refetch();
                void byUploaderPrevQ.refetch();
              },
            }}
          />
        ) : rows.length === 0 ? (
          // Mes sin datos: escalera visible abajo, tabla vacía con UN botón.
          <EmptyState
            icon={<BarChart3 className="w-6 h-6" aria-hidden />}
            title={t('f5t6a.ap.emptyMonth', { period: periodWord })}
            body={t('f5t6a.ap.emptyMonthBody')}
            action={
              period === 'current'
                ? { label: t('f5t6a.ap.emptyPrev'), onClick: () => { setPeriod('previous'); setPage(0); } }
                : undefined
            }
          />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr>
                    <Th first>{t('f5t6a.ap.col.person')}</Th>
                    <Th>{t(tab === 'agents' ? 'f5t6a.ap.col.month' : 'f5t6a.ap.col.uploadsMonth')}</Th>
                    {tab === 'agents' && <Th>{t('f5t6a.ap.col.tier')}</Th>}
                    {tab === 'agents' && <Th>{t('f5t6a.ap.col.toNext')}</Th>}
                    <Th>{t('f5t6a.ap.col.avg')}</Th>
                    <Th>{t('f5t6a.ap.col.pace')}</Th>
                    <Th>{t('f5t6a.ap.col.days')}</Th>
                  </tr>
                </thead>
                <tbody>
                  {pageRows.length === 0 ? (
                    // Filtro sin resultados: UNA línea, sin vaciar la distribución.
                    <tr>
                      <td colSpan={tab === 'agents' ? 7 : 5} className="px-6 h-[56px] text-[13px] text-nav-slate border-t border-nav-hair">
                        {t('f5t6a.ap.noTierRows')}
                      </td>
                    </tr>
                  ) : (
                    pageRows.map((r, i) => {
                      const selected = (tab === 'agents' && drillAgent != null && String(drillAgent) === r.id) || (tab === 'uploaders' && drillUploader === r.id);
                      const pace = paceText(r);
                      const week = weekById.get(r.id);
                      const pacePct = week != null && weekGoal ? Math.min(100, (week / weekGoal) * 100) : 0;
                      return (
                        <tr
                          key={r.id}
                          tabIndex={0}
                          onClick={() => openRow(r)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') openRow(r);
                          }}
                          style={{ animationDelay: `${i * 0.06}s` }}
                          className={cn(
                            'cursor-pointer animate-fade-up motion-reduce:animate-none transition-colors duration-150 motion-reduce:transition-none',
                            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal focus-visible:ring-inset',
                            selected ? 'bg-nav-tealTint hover:bg-[#d3e8e9]' : 'hover:bg-[#f4fafa]',
                          )}
                        >
                          <td className="pl-6 pr-[18px] h-[56px] border-t border-nav-hair">
                            <span className="flex items-center gap-[11px] min-w-0">
                              <span aria-hidden className="w-[30px] h-[30px] rounded-full bg-nav-navy text-white font-head text-[11px] font-bold flex items-center justify-center flex-none">
                                {initials(r.name)}
                              </span>
                              <span className="font-head text-[13.5px] font-bold tracking-[-0.01em] truncate">{r.name}</span>
                            </span>
                          </td>
                          <td className="px-[18px] h-[56px] border-t border-nav-hair font-head font-bold tabular-nums">{r.total}</td>
                          {tab === 'agents' && (
                            <td className="px-[18px] h-[56px] border-t border-nav-hair whitespace-nowrap">
                              {r.tier ? (
                                // Palabra SIEMPRE al lado del color (a11y).
                                <span className="inline-flex items-center gap-2 font-head text-[13px] font-bold">
                                  <span aria-hidden className="w-[11px] h-[11px] rounded-[3px] flex-none" style={{ backgroundColor: LADDER_COLOR[r.tier] }} />
                                  {t(TIER_LABEL_KEY[r.tier])}
                                </span>
                              ) : (
                                '—'
                              )}
                            </td>
                          )}
                          {tab === 'agents' && (
                            <td className="px-[18px] h-[56px] border-t border-nav-hair text-[13px] text-nav-slate tabular-nums whitespace-nowrap">
                              {toNextLabel(r)}
                            </td>
                          )}
                          {/* NECESITA-SERVER: «Promedio diario» = total/días
                              activos — los días activos por persona no viajan
                              en ningún endpoint admin. Guion, no cero (para no
                              confundir «no trabajó» con «trabajó y no envió»). */}
                          <td className="px-[18px] h-[56px] border-t border-nav-hair text-[13px] text-nav-slate tabular-nums">—</td>
                          <td className="px-[18px] h-[56px] border-t border-nav-hair whitespace-nowrap">
                            {pace != null ? (
                              <span className="inline-flex items-center gap-2">
                                <span aria-hidden className="w-[56px] h-1.5 rounded-[3px] bg-nav-hair overflow-hidden flex-none">
                                  <span
                                    className="block h-full bg-nav-teal transition-[width] duration-300 ease-[cubic-bezier(.4,0,.2,1)] motion-reduce:transition-none"
                                    style={{ width: `${pacePct}%` }}
                                  />
                                </span>
                                <span className="text-[13px] text-nav-slate tabular-nums">{pace}</span>
                              </span>
                            ) : (
                              <span className="text-[13px] text-nav-slate">—</span>
                            )}
                          </td>
                          {/* NECESITA-SERVER: «Días activos» por persona. */}
                          <td className="px-[18px] h-[56px] border-t border-nav-hair text-[13px] text-nav-slate tabular-nums">—</td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-between gap-3 flex-wrap px-6 py-4 border-t border-nav-hair">
              <span className="text-[13px] text-nav-slate tabular-nums">{footLabel}</span>
              <span className="text-[13px] text-nav-slate">{t('f5t6a.ap.rowHint')}</span>
            </div>
            <Pager
              page={pageClamped}
              pages={pages}
              onPage={setPage}
              prevLabel={t('f5t6a.pager.prev')}
              nextLabel={t('f5t6a.pager.next')}
              pageLabel={t('f5t6a.pager.page', { p: pageClamped + 1, pp: pages })}
              className="px-6 pb-4"
            />
          </>
        )}
      </div>

      {/* ---- Nota uploaders — disclaimer permanente (contrato) ---- */}
      {tab === 'uploaders' && (
        <div className="flex gap-2.5 bg-white border-[1.5px] border-nav-card rounded-xl px-[18px] py-4 mt-3.5 text-[13.5px] leading-relaxed text-nav-slate">
          <Info className="w-[17px] h-[17px] flex-none mt-0.5" strokeWidth={2} aria-hidden />
          <p className="m-0">{t('f5t6a.ap.uploaderNote')}</p>
        </div>
      )}

      {/* ---- La escalera — SOLO Agentes; umbrales VIGENTES de config ---- */}
      {tab === 'agents' && ladder && (
        <div className="bg-white border-[1.5px] border-nav-card rounded-xl px-6 pt-[22px] pb-6 mt-6">
          <h2 className="m-0 font-head text-[10.5px] font-bold tracking-[0.14em] uppercase text-nav-teal">
            {t('f5t6a.ap.ladderTitle')}
          </h2>
          <p className="mt-2 mb-0 text-[13.5px] leading-relaxed text-nav-slate max-w-[640px]">{t('f5t6a.ap.ladderBody')}</p>
          <div className="grid gap-3.5 [grid-template-columns:repeat(auto-fit,minmax(190px,1fr))] mt-4">
            {(['base', 't1', 't2', 't3'] as const).map((tier) => {
              const min = ladder[tier];
              const next = tier === 'base' ? ladder.t1 : tier === 't1' ? ladder.t2 : tier === 't2' ? ladder.t3 : null;
              const range = next != null ? `${min}–${next - 1}` : t('f5t6a.ap.orMore', { n: min });
              return (
                <div key={tier} className="rounded-xl border-[1.5px] border-nav-card bg-nav-softer px-[18px] py-4">
                  <span className="flex items-center gap-2 font-head text-[14px] font-bold">
                    <span aria-hidden className="w-[13px] h-[13px] rounded-[4px] flex-none" style={{ backgroundColor: LADDER_COLOR[tier] }} />
                    {t(TIER_LABEL_KEY[tier])}
                  </span>
                  <span className="block mt-2 font-head text-[20px] font-extrabold tracking-[-0.01em] tabular-nums">{range}</span>
                  <span className="block mt-1 text-[12.5px] text-nav-slate">{t('f5t6a.ap.ladderSub')}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

        </>
      )}

      {/* Drill-down de uploaders — conservado (el de agentes pasó a la
          ficha F5-R8c de arriba). */}
      <UploaderDetailModal uploaderId={drillUploader} onClose={() => setDrillUploader(null)} />
    </div>
  );
}

function Th({ children, first }: { children: ReactNode; first?: boolean }): JSX.Element {
  return (
    <th
      className={cn(
        'h-[38px] px-[18px] text-left font-head text-[10.5px] font-bold tracking-[0.1em] uppercase text-nav-slate bg-nav-softer border-y border-nav-hair whitespace-nowrap',
        first && 'pl-6',
      )}
    >
      {children}
    </th>
  );
}
